import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CREATE_ORDER_STEPS,
  EMPTY_ORDER_DRAFT,
  eligibleOrderablePackages,
  orderablePackagesByIdParams,
  isStepComplete,
  NEW_ORDER_DEFAULT_PAYMENT_MODE,
  assessPromoCode,
  insuranceChosen,
  receiptHold,
  receiptsWanted,
  type OrderablePackage,
  type OrderDraft,
  type ShippingChannel,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { MAX_PACKAGES_PER_ORDER } from '@/features/orders/fixtures.js';
import { ChannelScopeNotice, ChannelStep } from '@/features/orders/components/ChannelStep.js';
import { CatalogErrorBanner } from '@/features/orders/components/CatalogErrorBanner.js';
import { ConfirmSummary } from '@/features/orders/components/ConfirmSummary.js';
import { DeliveryForm } from '@/features/orders/components/DeliveryForm.js';
import { ORDERS_ROUTE } from '@/shell/navigation.js';
import { OrderablePackagesStep } from '@/features/orders/components/OrderablePackagesStep.js';
import { PoolState } from '@/features/orders/components/PackagesStep.js';
import { StepIndicator } from '@/features/orders/components/StepIndicator.js';
import { OrderFlowBar, OrderFlowHeader } from '@/features/orders/components/OrderFlowLayout.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { useSelectionWeight } from '@/features/orders/useSelectionWeight.js';
import { useFormat } from '@/lib/format.js';
import { useAccountSession } from '@/features/account/index.js';
import { useScrollToTopOnChange } from '@/lib/scroll.js';
import { useOrderCatalog } from '@/features/orders/catalogContext.js';
import { useOrderServices } from '@/features/orders/orderServicesContext.js';
import { useOrders, type OrderCreateOutcome } from '@/features/orders/context.js';
import { useReceiptUploads } from '@/features/orders/useReceiptUploads.js';
import { useScopedChannels } from '@/features/orders/useScopedChannels.js';
import { useScopedServices } from '@/features/orders/useScopedServices.js';
import { useAutoTour, useTourStep, useTours } from '@/features/tour/index.js';
import { usePackages } from '@/features/packages/index.js';
import { fetchPackagePage } from '@/features/packages/api.js';
import { useCoupons } from '@/features/coupons/index.js';
import { useWarehouses } from '@/features/warehouses/index.js';

/** A submit that did not create an order, as the Confirm step explains it. */
type SubmitFailure = Exclude<OrderCreateOutcome, { kind: 'created' }>;

/**
 * Create order — one page carrying all four steps.
 *
 * The legacy system spreads this across separate pages under separate menu entries; the design we
 * build to folds them into a single flow with a step strip. One page also means a half-filled
 * order survives moving between steps, which separate routes would have to solve with shared
 * state anyway.
 *
 * Every choice lives in one `OrderDraft` rather than a state variable per field, because the
 * Confirm step has to summarise all of it and jump back to whichever step owns a given value.
 */
export function CreateOrder() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const weightLabel = useSelectionWeight();
  const navigate = useNavigate();
  const { submit } = useOrders();
  const { toPackages, dictionariesStatus, listVersion, reload: reloadPackages } = usePackages();
  const catalog = useOrderCatalog();
  const { services } = useOrderServices();
  const [step, setStep] = useState(0);
  // MYP-118: every way of changing step (Continue, the step strip, Confirm's Edit links, Submit
  // sending the reader back) opens the new step at its top, with focus at its start.
  const stepTop = useRef<HTMLDivElement>(null);
  useScrollToTopOnChange(step, stepTop);

  /*
    MYP-83: a first-time tour of the four steps. While it is open the page shows the step the tour
    is on, not its own — `step`, the draft and the scroll position are untouched, so closing it
    puts the reader back exactly where they were.
  */
  const tours = useTours();
  useAutoTour('create_order');
  const shownStep = useTourStep('create_order') ?? step;

  /*
    MYP-130: Step 1 pages through the orderable packages on the server, so no page holds them all.
    `known` keeps every orderable package this page has seen — each list page, and the packages
    carried over from the Packages page — so a package ticked on page 1 still counts towards the
    weight, the channel lookup and the Confirm step from page 3.
  */
  const [known, setKnown] = useState<ReadonlyMap<string, OrderablePackage>>(() => new Map());
  const remember = useCallback((seen: readonly OrderablePackage[]) => {
    setKnown((current) => {
      if (seen.every((item) => current.get(item.id) === item)) return current;
      const next = new Map(current);
      for (const item of seen) next.set(item.id, item);
      return next;
    });
  }, []);
  const statusCodes = catalog.status === 'loading' ? undefined : catalog.eligiblePackageStatusCodes;

  // Packages carried over from the Packages list's own selection (see Packages.tsx's "Create
  // order" bar), read once on mount the same way Orders reads a just-submitted order number back
  // out of router state. Capped at the per-order limit here; which of them can still be ordered is
  // checked below, once the orderable statuses are known.
  const incomingPackageIds = (useLocation().state as { packageIds?: readonly string[] } | null)
    ?.packageIds;

  const [draft, setDraft] = useState<OrderDraft>(() => ({
    ...EMPTY_ORDER_DRAFT,
    // MANUAL until the customer picks Automatic on Step 3 — see NEW_ORDER_DEFAULT_PAYMENT_MODE.
    paymentMode: NEW_ORDER_DEFAULT_PAYMENT_MODE,
    packageIds: incomingPackageIds
      ? incomingPackageIds.slice(0, MAX_PACKAGES_PER_ORDER)
      : EMPTY_ORDER_DRAFT.packageIds,
  }));
  const receipts = useReceiptUploads();

  const [servicesCleared, setServicesCleared] = useState(false);
  const patch = useCallback((next: Partial<OrderDraft>) => {
    // A change the customer makes to the services answers the "cleared" note; it can go.
    if (next.serviceIds) setServicesCleared(false);
    setDraft((current) => ({ ...current, ...next }));
  }, []);

  /*
    Ticked packages that can no longer go on a new order — already on one, or not arrived/in the
    warehouse — are unticked, and counted, so the customer is told rather than finding fewer ticks
    than they left with (MYP-90). One `pkgId` request answers which can still be ordered (MYP-130);
    its rows also fill `known`. It runs:
    - once at the start, for the packages carried over from the Packages page;
    - after every reload of the package list (`listVersion`), for whatever is ticked then. A submit
      the vendor refused as "not eligible", or whose answer was lost, reloads the list — a package
      ordered elsewhere in the meantime is unticked here instead of being sent again.
    It waits while the catalog has failed: with no orderable statuses it cannot tell, and
    unticking everything would lose the customer's choice on top of the catalog error.
  */
  const [droppedTicked, setDroppedTicked] = useState(0);
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [checkFailed, setCheckFailed] = useState(false);
  const [firstVersion] = useState(listVersion);
  const checkedFor = useRef<string | undefined>(undefined);
  const tickedNow = useRef(draft.packageIds);
  useEffect(() => {
    tickedNow.current = draft.packageIds;
  });
  useEffect(() => {
    if (statusCodes === undefined || dictionariesStatus === 'loading' || catalog.status === 'error')
      return;
    const token = listVersion === firstVersion ? 'carried' : `reload ${listVersion}`;
    if (checkedFor.current === token) return;
    const ids =
      token === 'carried'
        ? (incomingPackageIds ?? []).slice(0, MAX_PACKAGES_PER_ORDER)
        : tickedNow.current;
    if (ids.length === 0) {
      checkedFor.current = token;
      return;
    }
    let current = true;
    const request =
      statusCodes.length === 0
        ? Promise.resolve({ rows: [] as readonly unknown[], count: 0 })
        : fetchPackagePage(orderablePackagesByIdParams(statusCodes, ids));
    request.then(
      (result) => {
        if (!current) return;
        checkedFor.current = token;
        setCheckFailed(false);
        const orderable = eligibleOrderablePackages(toPackages(result.rows), statusCodes);
        remember(orderable);
        const ok = new Set(orderable.map((item) => item.id));
        const missing = new Set(ids.filter((id) => !ok.has(id)));
        if (missing.size === 0) return;
        setDraft((draftNow) => ({
          ...draftNow,
          packageIds: draftNow.packageIds.filter((id) => !missing.has(id)),
        }));
        setDroppedTicked(missing.size);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not check the ticked packages', cause);
        setCheckFailed(true);
      },
    );
    return () => {
      current = false;
    };
  }, [
    statusCodes,
    dictionariesStatus,
    catalog.status,
    listVersion,
    firstVersion,
    incomingPackageIds,
    toPackages,
    remember,
    checkAttempt,
  ]);

  const selected = useMemo(
    () =>
      draft.packageIds.flatMap((id) => {
        const item = known.get(id);
        return item ? [item] : [];
      }),
    [known, draft.packageIds],
  );

  /** Every ticked package is one this page has the details of. */
  const tickedKnown = selected.length === draft.packageIds.length;

  /*
    The channels on offer depend on the packages (MYP-87): one warehouse, one goods type. A channel
    chosen for one selection is not necessarily offered for the next, so once the new selection's
    list is known, a channel no longer in it is cleared — and the channel step says why — rather
    than carried into an order the vendor would refuse.
  */
  const scoped = useScopedChannels(selected);
  const [channelCleared, setChannelCleared] = useState(false);
  const channelOffered =
    draft.channelId !== undefined &&
    scoped.status === 'ready' &&
    scoped.channels.some((channel) => channel.id === draft.channelId);
  const channelWithdrawn =
    draft.channelId !== undefined &&
    (scoped.result.kind !== 'ok' || (scoped.status === 'ready' && !channelOffered));

  useEffect(() => {
    if (!channelWithdrawn) return;
    setDraft((current) => ({ ...current, channelId: undefined }));
    setChannelCleared(true);
  }, [channelWithdrawn]);

  /*
    Services are per warehouse (MYP-89), and the warehouse is the one the channel lookup settled
    on. Ticked services the warehouse does not offer — ticked for an earlier selection — are
    cleared once its list is known, and the services card says why.
  */
  const servicesView = useScopedServices(
    scoped.result.kind === 'ok' ? scoped.result.scope.warehouseId : undefined,
  );
  const withdrawnServices =
    servicesView.kind === 'ready'
      ? draft.serviceIds
          .filter((id) => !servicesView.offered.some((service) => service.id === id))
          .join(',')
      : '';

  useEffect(() => {
    if (withdrawnServices === '') return;
    const withdrawn = withdrawnServices.split(',');
    setDraft((current) => ({
      ...current,
      serviceIds: current.serviceIds.filter((id) => !withdrawn.includes(id)),
    }));
    setServicesCleared(true);
  }, [withdrawnServices]);

  /** Everything a channel id on this page could name: the offered list first, then the dictionary. */
  const knownChannels = useMemo<readonly ShippingChannel[]>(() => {
    const seen = new Set(scoped.channels.map((channel) => channel.id));
    return [...scoped.channels, ...catalog.channels.filter((channel) => !seen.has(channel.id))];
  }, [scoped.channels, catalog.channels]);

  /*
    The promo code (MYP-95), checked against the customer's vouchers and the chosen channel's
    transport. A code already applied that stops fitting (the channel changed from air to sea, say)
    is taken off the draft and the field says why: a code that could not apply is never sent.
  */
  const { coupons, status: couponsStatus } = useCoupons();
  const chosenChannel = knownChannels.find((channel) => channel.id === draft.channelId);
  const transportType = chosenChannel?.transportType;
  const chosenChannelName = chosenChannel?.name;
  const assessPromo = useCallback(
    (code: string) => assessPromoCode(code, coupons, transportType, new Date()),
    [coupons, transportType],
  );
  const [promoCleared, setPromoCleared] = useState(false);
  useEffect(() => {
    if (draft.couponCode === '' || assessPromo(draft.couponCode).kind !== 'rejected') return;
    setDraft((current) => ({ ...current, couponCode: '' }));
    setPromoCleared(true);
  }, [draft.couponCode, assessPromo]);

  /*
    Submitting is a real request now (MYP-91), and a second one is a second real order. The ref is
    the guard — two clicks can land before the button re-renders disabled — and the state is what
    the button shows.
  */
  /*
    Purchase receipts (MYP-96), asked for only where they are needed (MYP-140). The rule — which
    packages need one, and whether that holds Step 1 or Step 3 — is `receiptHold`, tested in
    @mypoz/shared; this page only feeds it the ticked packages and acts on the answer. Every
    ticked package is passed, its warehouse once known: an upload on a package unticked mid-way is
    not on screen, so it must not block.
  */
  const { warehouses } = useWarehouses();
  const countryOf = useCallback(
    (warehouseId: string | undefined) =>
      warehouses.find((warehouse) => warehouse.id === warehouseId)?.country,
    [warehouses],
  );
  const insured = insuranceChosen(draft.serviceIds, services);
  const wantsReceipt = useCallback(
    (item: OrderablePackage) =>
      receiptsWanted(item.warehouseId, countryOf(item.warehouseId), insured),
    [insured, countryOf],
  );
  const hold = receiptHold({
    packages: draft.packageIds.map((id) => ({ id, warehouseId: known.get(id)?.warehouseId })),
    countryOf,
    insured,
    paths: receipts.paths,
    uploadingIds: receipts.uploadingIds,
  });
  const missingReceipts = hold.missing;

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [submitFailure, setSubmitFailure] = useState<SubmitFailure | null>(null);

  const { refresh: refreshAccount } = useAccountSession();

  const submitOrder = async () => {
    // Reachable from the step strip with a step left undone; send them back to it.
    if (scoped.result.kind !== 'ok' || !isStepComplete(draft, 'packages') || !tickedKnown) {
      setStep(0);
      return;
    }
    if (!isStepComplete(draft, 'channel')) {
      setStep(1);
      return;
    }
    if (!isStepComplete(draft, 'delivery')) {
      setStep(2);
      return;
    }
    // Reachable from the step strip with receipts still missing or uploading. The warehouse's go
    // back to Step 1; insurance's to Step 3, which says what is missing and links to Step 1.
    if (hold.step1Blocked) {
      setStep(0);
      return;
    }
    if (hold.step3Blocked) {
      setStep(2);
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitFailure(null);

    // Receipts go only where the vendor keeps them; anything picked before insurance was taken off
    // a China or Malaysia order stays behind rather than being sent to be dropped.
    const warehouseId = scoped.result.scope.warehouseId;
    const sendReceipts = receiptsWanted(warehouseId, countryOf(warehouseId), insured);
    const outcome = await submit(draft, warehouseId, services, sendReceipts ? receipts.paths : {});

    submittingRef.current = false;
    setSubmitting(false);

    if (outcome.kind === 'created') {
      // The packages are on an order now, and no longer free to order.
      reloadPackages();
      navigate(ORDERS_ROUTE, { state: { submitted: outcome.orderNumber ?? true } });
      return;
    }

    /*
      Re-read the packages when the answer may mean they changed: a lost answer may have put them
      on an order, and -1/-2 includes "packages not eligible" — ordered elsewhere since this page
      loaded. Either way the pool then shows what can still be ordered (MYP-90).
    */
    if (
      outcome.kind === 'unconfirmed' ||
      (outcome.kind === 'refused' && isIneligible(outcome.code))
    ) {
      reloadPackages();
    }
    setSubmitFailure(outcome);
    /*
      MYP-142's safety net. The vendor has no "not verified" code for orders_create, so on any
      refusal the account is read again: if it now says not verified (verification withdrawn since
      the page opened), VerificationGate replaces this page with the reason and the link.
    */
    if (outcome.kind === 'refused') void refreshAccount();
  };

  const togglePackage = (packageId: string) =>
    setDraft((current) => ({
      ...current,
      packageIds: current.packageIds.includes(packageId)
        ? current.packageIds.filter((id) => id !== packageId)
        : current.packageIds.length >= MAX_PACKAGES_PER_ORDER
          ? current.packageIds
          : [...current.packageIds, packageId],
    }));

  const stepKey = CREATE_ORDER_STEPS[shownStep] ?? 'packages';
  // A selection whose channels cannot be looked up cannot go on to choose one.
  const canContinue =
    isStepComplete(draft, stepKey) &&
    // A ticked package not checked yet (carried over, still being looked up) has no weight,
    // warehouse or goods type to go on with.
    (stepKey !== 'packages' || tickedKnown) &&
    (stepKey !== 'packages' || scoped.result.kind === 'ok') &&
    // A warehouse that needs receipts, or an upload in flight, holds Step 1; insurance's, Step 3.
    !(stepKey === 'packages' && hold.step1Blocked) &&
    !(stepKey === 'delivery' && hold.step3Blocked);

  const steps = [
    t('orders.newOrder.step.packages'),
    t('orders.newOrder.step.channel'),
    t('orders.newOrder.step.delivery'),
    t('orders.newOrder.step.confirm'),
  ];

  return (
    <>
      <OrderFlowHeader
        title={t('orders.newOrder.title')}
        stepper={
          <StepIndicator
            steps={steps}
            current={shownStep}
            completed={CREATE_ORDER_STEPS.map(
              (key) => key !== 'confirm' && isStepComplete(draft, key),
            )}
            onSelect={setStep}
          />
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => tours.start('create_order')}
              className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0 lg:min-w-0"
            >
              <Icon name="help" size={18} />
              {/* Icon only on a phone, where the title needs the width; still named for a screen reader. */}
              <span className="sr-only sm:not-sr-only">{t('tour.howItWorks')}</span>
            </button>

            <Link
              to={ORDERS_ROUTE}
              className="inline-flex min-h-11 shrink-0 items-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0"
            >
              <Icon name="close" size={18} />
              {t('orders.newOrder.cancel')}
            </Link>
          </>
        }
      />

      <CatalogErrorBanner />

      <div ref={stepTop} tabIndex={-1} className="outline-none">
        {shownStep === 0 && checkFailed ? (
          <PoolState
            status="error"
            onRetry={() => {
              setCheckFailed(false);
              setCheckAttempt((previous) => previous + 1);
            }}
          />
        ) : null}

        {shownStep === 0 && !checkFailed ? (
          <OrderablePackagesStep
            statusCodes={statusCodes}
            selected={selected}
            onSeen={remember}
            selectedIds={draft.packageIds}
            receipts={receipts.slots}
            onToggle={togglePackage}
            onAttach={receipts.attach}
            onRetryReceipt={receipts.retry}
            onRemoveReceipt={receipts.remove}
            wantsReceipt={wantsReceipt}
            receiptReason={hold.reason}
          />
        ) : null}

        {(shownStep === 0 || shownStep === 3) && droppedTicked > 0 ? (
          <p role="status" className="mt-4 border-l-2 border-warning pl-3 text-sm text-ink">
            {t('orders.picker.packages.droppedCarried', { count: droppedTicked })}
          </p>
        ) : null}

        {shownStep === 0 ? <ChannelScopeNotice result={scoped.result} className="mt-4" /> : null}

        {shownStep === 1 ? (
          <ChannelStep
            scoped={scoped}
            selectedId={draft.channelId}
            cleared={channelCleared}
            onSelect={(channelId) => {
              setChannelCleared(false);
              patch({ channelId });
            }}
          />
        ) : null}

        {shownStep === 2 ? (
          <DeliveryForm
            countries={catalog.countries}
            provincesFor={catalog.provincesFor}
            provincesStatus={catalog.provincesStatus}
            citiesFor={catalog.citiesFor}
            citiesStatus={catalog.citiesStatus}
            loadCities={catalog.loadCities}
            servicesView={servicesView}
            servicesCleared={servicesCleared}
            draft={draft}
            onPatch={patch}
            promo={{
              appliedCode: draft.couponCode,
              assess: assessPromo,
              onApply: (code) => {
                setPromoCleared(false);
                patch({ couponCode: code });
              },
              onRemove: () => patch({ couponCode: '' }),
              vouchersLoading: couponsStatus === 'loading',
              cleared: promoCleared,
            }}
          />
        ) : null}

        {/* Insurance needs a receipt on every package (MYP-140); they are attached on Step 1. */}
        {shownStep === 2 && insured && missingReceipts > 0 ? (
          <div role="status" className="mt-4 border-l-2 border-warning pl-3 text-sm text-ink">
            <p>{t('orders.newOrder.receipt.insuranceNeeds', { count: missingReceipts })}</p>
            <button
              type="button"
              onClick={() => setStep(0)}
              className="mt-1 min-h-11 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0"
            >
              {t('orders.newOrder.receipt.goToStep1')}
            </button>
          </div>
        ) : null}

        {shownStep === 3 ? (
          <ConfirmSummary
            draft={draft}
            pool={selected}
            channels={knownChannels}
            services={services}
            countries={catalog.countries}
            provincesFor={catalog.provincesFor}
            citiesFor={catalog.citiesFor}
            onEdit={setStep}
            onSubmit={() => void submitOrder()}
            submitting={submitting}
            error={
              submitFailure ? (
                <SubmitFailureMessage failure={submitFailure} promoCode={draft.couponCode} />
              ) : null
            }
          />
        ) : null}
      </div>

      {/*
        MYP-131: what is chosen, and the way on, pinned to the bottom of the screen. Confirm carries
        its own Submit, so Continue stops at the third step; Back is there from the second.
      */}
      <OrderFlowBar>
        {shownStep > 0 ? (
          <button
            type="button"
            onClick={() => setStep((current) => Math.max(current - 1, 0))}
            className={actionLinkClass()}
          >
            <Icon name="arrow_back" size={18} />
            {t('orders.newOrder.back')}
          </button>
        ) : null}
        {/*
          The summary and, under it, the receipts still missing — on Step 1, and on Step 3 when
          insurance needs them — with Continue beside them. The summary's option is not named
          `count`: i18next reserves that for pluralisation.
        */}
        <div className="min-w-0 flex-1 sm:flex sm:flex-wrap sm:items-baseline sm:gap-x-4">
          <p className="min-w-0 text-sm text-ink tabular-nums">
            <span className="font-semibold">
              {t('orders.newOrder.summary', {
                selected: fmt.number(selected.length),
                weight: weightLabel(selected),
              })}
            </span>
            {shownStep > 0 && chosenChannelName ? (
              <>
                {' · '}
                <span className="font-semibold">{chosenChannelName}</span>
              </>
            ) : null}
          </p>
          {(shownStep === 0 || (shownStep === 2 && insured)) && missingReceipts > 0 ? (
            <p role="status" className="text-xs font-semibold text-warning sm:text-sm">
              {t('orders.newOrder.receipt.missing', { count: missingReceipts })}
            </p>
          ) : null}
        </div>
        {shownStep < steps.length - 1 ? (
          <button
            type="button"
            disabled={!canContinue}
            onClick={() => setStep((current) => Math.min(current + 1, steps.length - 1))}
            className={buttonClass(
              'primary',
              'ml-auto px-5 disabled:cursor-not-allowed disabled:opacity-50 sm:px-8',
            )}
          >
            {t('orders.newOrder.continue')}
          </button>
        ) : null}
      </OrderFlowBar>
    </>
  );
}

/** `orders_create`'s -1/-2: details incomplete, or the packages are no longer eligible. */
function isIneligible(code: string): boolean {
  return code === '-1' || code === '-2';
}

/** The customer's line for each documented refusal code. 3–9 are one lumped line in the document. */
function refusalKey(
  code: string,
):
  | 'orders.newOrder.submitError.billing'
  | 'orders.newOrder.submitError.noOrderType'
  | 'orders.newOrder.submitError.notEligible'
  | 'orders.newOrder.submitError.refused'
  | 'orders.newOrder.submitError.receiptMissing'
  | 'orders.newOrder.submitError.receiptNotSaved' {
  if (code === '2') return 'orders.newOrder.submitError.billing';
  if (code === '-6') return 'orders.newOrder.submitError.receiptMissing';
  if (code === '-7') return 'orders.newOrder.submitError.receiptNotSaved';
  if (code === '11') return 'orders.newOrder.submitError.noOrderType';
  if (isIneligible(code)) return 'orders.newOrder.submitError.notEligible';
  return 'orders.newOrder.submitError.refused';
}

/**
 * Why a submit made no order — or, for a lost answer, why we cannot say. The vendor's own reason
 * goes beneath the translated line: for 3–9 and -1/-2 it is the only thing that says which failure
 * it was (MYP-91).
 */
function SubmitFailureMessage({
  failure,
  promoCode,
}: {
  failure: SubmitFailure;
  /** The code sent with the order, if any: a refusal may be the vendor rejecting it (MYP-95). */
  promoCode: string;
}) {
  const { t } = useTranslation();

  let title: string;
  let detail: string | undefined;
  if (failure.kind === 'refused') {
    title = t(refusalKey(failure.code));
    detail =
      failure.detail !== ''
        ? t('orders.newOrder.submitError.vendorSaid', { detail: failure.detail })
        : t('orders.newOrder.submitError.code', { code: failure.code });
  } else if (failure.kind === 'unconfirmed') {
    title = t('orders.newOrder.submitError.unconfirmed');
  } else {
    title = t('orders.newOrder.submitError.notSent');
  }

  return (
    <div role="alert" className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
      <p className="text-sm text-danger">{title}</p>
      {detail ? <p className="mt-1 text-xs text-muted">{detail}</p> : null}
      {failure.kind === 'refused' && promoCode !== '' ? (
        <p className="mt-1 text-sm text-danger">
          {t('orders.newOrder.submitError.promoHint', { code: promoCode })}
        </p>
      ) : null}
      {failure.kind === 'unconfirmed' ? (
        <Link to={ORDERS_ROUTE} className="mt-1 inline-block text-sm font-semibold text-brand">
          {t('orders.newOrder.submitError.checkOrders')}
        </Link>
      ) : null}
    </div>
  );
}
